import * as React from 'react';
import clsx from 'clsx';
import pick from 'lodash/pick';
import { Field } from 'react-final-form';

// eslint-disable-next-line @gandi/gandi/no-private-import

import { useTranslator } from '@gandi/translate';
import { Base, Button } from '@gandi/ui';
import { ErrorMessage } from '../../components/controls/ErrorMessage';
import { InputTextAny } from '../../components/controls/InputTextAny';
import { fieldConfigKeys } from '../decorateControl';
import { useFieldArray } from '../hooks/useFieldArray';
import { inputControlPropsBase } from '../utils/controlProps';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var styles = {
  "root": "InputTextAnyField-root_2-UPG",
  "fields": "InputTextAnyField-fields_1IjjU",
  "field": "InputTextAnyField-field_3M9zG",
  "buttonWrapper": "InputTextAnyField-buttonWrapper_fm-TL",
  "padded": "InputTextAnyField-padded_3zKJN",
  "button": "InputTextAnyField-button_994E2"
};
/**
 * Simple form group for InputTextAny
 */
function InputTextAnyGroup(props) {
  const {
    input,
    meta,
    ...extraProps
  } = props;
  const fieldProps = {
    ...input,
    ...extraProps
  };
  const hasError = (!!meta.error || !!meta.submitError) && meta.touched;
  return /*#__PURE__*/_jsxs("div", {
    className: hasError ? 'has-error' : '',
    children: [/*#__PURE__*/_jsx(InputTextAny, {
      ...fieldProps
    }), hasError && /*#__PURE__*/_jsx(ErrorMessage, {
      text: meta.error || meta.submitError
    })]
  });
}
/**
 * /!\ Important read: 
 *   - you need to inject mutators to your form in order to use this field
 *   - To display a first empty field by default, make sure `initialValues` has one element like described below
 *
 * import arrayMutators from 'final-form-arrays'
 * …
 * <Form
    onSubmit={onSubmit}
    mutators={{ ...arrayMutators, ...otherMutators }}
    initialValues={{<fieldname>: ['']}}
    >

 * cf. https://final-form.org/docs/react-final-form/migration/redux-form#fieldarray
 */

export function InputTextAnyFieldGroup({
  name,
  validateFieldArray,
  addMoreText,
  hint,
  maxFields = -1,
  controlPropsExtra,
  ...props
}) {
  const {
    disabled,
    readOnly
  } = props;
  const fieldProps = pick(props, fieldConfigKeys);
  const controlProps = {
    ...pick(props, inputControlPropsBase),
    ...controlPropsExtra
  };
  const {
    __
  } = useTranslator();
  const {
    fields,
    meta: {
      error
    }
  } = useFieldArray(name, {
    validate: validateFieldArray
  });

  // Unfortunatly error can be a string when handled by `validateFieldArray`
  // or an array when it has been handled by single field `validate` function
  // we only keep the string as the global error
  const fieldArrayError = Array.isArray(error) ? undefined : error;

  // $FlowFixMe[unnecessary-optional-chain] cf. https://github.com/final-form/react-final-form-arrays/pull/174
  const hasManyFields = (fields.value?.length || 0) > 1;
  const buttonWrapperClasses = clsx(styles.buttonWrapper, {
    [styles.padded]: hasManyFields
  });
  const displayAddFieldLink = !disabled && !readOnly && (
  // $FlowFixMe[unnecessary-optional-chain] cf. https://github.com/final-form/react-final-form-arrays/pull/174
  maxFields === -1 || maxFields > 0 && fields.value?.length < maxFields);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.root,
    children: [/*#__PURE__*/_jsx(Base, {
      tagName: "ul",
      className: styles.fields,
      flex: true,
      flexWrap: true,
      gap: "small",
      children: fields.map((fieldName, index) => {
        const hasRemoveButton = hasManyFields && !disabled && !readOnly;
        return /*#__PURE__*/_jsx("li", {
          className: styles.field,
          children: /*#__PURE__*/_jsx(Field, {
            name: fieldName,
            ...fieldProps,
            ...controlProps,
            component: InputTextAnyGroup,
            hasRemoveButton: hasRemoveButton,
            removeButtonTitle: __('Remove field'),
            handleRemoveButton: () => {
              fields.remove(index);
            }
          })
        }, `field-${fieldName}`);
      })
    }), displayAddFieldLink && /*#__PURE__*/_jsx(Base, {
      marginTop: "xsmall",
      className: buttonWrapperClasses,
      children: /*#__PURE__*/_jsx(Button, {
        type: "button",
        kind: "link",
        onClick: () => fields.push(''),
        children: addMoreText || __('Add a field')
      })
    }), !!fieldArrayError && /*#__PURE__*/_jsx(ErrorMessage, {
      text: fieldArrayError
    }), !!hint && /*#__PURE__*/_jsx(Base, {
      color: "light",
      size: "small",
      marginTop: "small",
      children: hint
    })]
  });
}